import { Component, Host, h, Element, Prop, Event, EventEmitter, Watch } from '@stencil/core';
import moveSlotChildElementsToLightDom from '../../utils/moveSlotChildElementsToLightDom';

export interface AhaRadioButton extends HTMLAhaRadioButtonElement {}

/**
 * The `<aha-radio-button>` serves as a fancy single choice toggle button. It is usually used as part of a group of buttons, and typically lives inside an `<aha-radio-button-group>` element where default styling will be applied to the `<aha-radio-button>`.
 *
 * Note that an `<aha-radio-button>` can be used within a `<form>` element, just the same as an `<input type="radio" />` by using the `name` and `value` attributes.
 *
 * @slot default - Contents of the default slot.
 */
@Component({
  tag: 'aha-radio-button',
  styleUrl: 'aha-radio-button.css',
  shadow: true,
})
export class AhaRadioButton {
  @Element() host!: HTMLAhaRadioButtonElement;

  /** Optional. The name of the backing `<input type="radio">` that will be sent to the server as part of a regular `<form>` submit. */
  @Prop({ reflect: true }) name?: string;

  /** Optional. The value of the backing `<input type="radio">` that will be sent to the server as part of a regular `<form>` submit. The `name` attribute can also be set on the parent `<aha-radio-button-group>` element, which will then give any unnamed `<aha-radio-button>` elements that name. */
  @Prop({ reflect: true }) value?: string;

  /** Whether or not the button is currently selected. */
  @Prop({ reflect: true }) selected?: boolean;
  @Watch('selected')
  private handleSelected(value: any) {
    if (this.innerElement) this.innerElement.checked = value;
    if (value) this.changeEvent.emit(this.host);
  }

  protected innerElement?: HTMLInputElement;

  //** Typically clicking on an `<aha-radio-button>` will cause that button to become selected. Sometimes this is not the desired behavior. Setting `preventClick` to true will make it such that the only way to set the `<aha-radio-button>` to selected is by setting the property/attribute directly. */
  @Prop({ reflect: true, attribute: 'prevent-click' }) preventClick?: boolean;

  /**
   * Triggers when the `<aha-radio-button>` is selected. Note that there is no corresponding "deselected" event.
   */
  @Event({
    eventName: 'aha-radio-button:change',
    cancelable: true,
    bubbles: true,
    composed: true,
  }) //eslint-disable-line @stencil/decorators-style
  changeEvent!: EventEmitter;

  /**
   * Triggers when the `<aha-radio-button>` is connected to the document. Note that there is no corresponding "deselected" event.
   */
  @Event({
    eventName: 'aha-radio-button:initialRender',
    cancelable: true,
    bubbles: true,
    composed: true,
  }) //eslint-disable-line @stencil/decorators-style
  initialRender!: EventEmitter;

  connectedCallback() {
    this.handleClick = this.handleClick.bind(this.host);

    const group = this.host.closest('aha-radio-button-group');
    if (group && !this.name) {
      const name = group.getAttribute('name');
      if (name) this.name = name;
    }
  }

  componentDidLoad() {
    // Move nodes inside slots into the light DOM. Used for `<form>` submission as well as styling of `<input>` elements.
    moveSlotChildElementsToLightDom(this.host, 'form-control');

    // Dispatch the change event if the radio button is initially rendered. This ensures that any listeners that are listening for the change event will be notified of the current state of the radio button.
    // This is useful for when the radio button is selected by default on page load.
    if (this.selected) {
      this.initialRender.emit(this.host);
    }
  }

  private handleClick() {
    if (this.preventClick) return;
    this.selected = true;
  }

  render() {
    return (
      <Host role="radio" aria-checked={this.selected ? 'true' : 'false'}>
        <button type="button" onClick={this.handleClick}>
          <slot name="form-control">
            <input
              ref={e => (this.innerElement = e)}
              type="radio"
              slot="form-control"
              name={this.name}
              value={this.value}
              checked={this.selected}
            />
          </slot>
          <slot></slot>
        </button>
      </Host>
    );
  }
}
